import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Moment } from "moment";
import { useRouter } from "@miplatform/hooks";
import {
  CalendarEventType,
  HubStatuses,
  IAttempt,
  IAttemptListItemResponseForWorkOrder,
  IAttemptListResponse,
  IAttemptTrayConfig,
  ICalendarConfig,
  IDateRangeISO,
  IUserListItemResponse,
} from "@miplatform/types";
import { useSignalR } from "@mibuildweb/hooks";
import { CalendarSetsConfigDataItem } from "../../stores/calendarsStore/calendarsStore.types";
import { useCalendarProviderViewModel } from "./useCalendarProviderViewModel";

const CalendarContext = createContext<CalendarContextType | undefined>(
  undefined
);

export const CalendarProvider: React.FC<CalendarProviderProps> = ({
  children,
}) => {
  const { params } = useRouter();
  const { calendarSetId, calendarId: initialCalendarId } = params;

  const [selectedUser, setSelectedUser] =
    useState<IUserListItemResponse | null>(null);

  const handleSelectUser = (user: IUserListItemResponse | null) => {
    setSelectedUser(user);
  };

  const {
    events,
    zoom,
    assignableUsers,
    schedulerUsers,
    activeCalendarId,
    calendarSet,
    calendarConfig,
    date,
    dateRange,
    search,
    debouncedSearch,
    trayBuckets,
    allTrayEvents,
    isLoading: isCalendarLoading,
    setCalendarDate,
    onDateRangeChange: setDateRange,
    setZoom,
    handleLoadEvents,
    onAttemptsUpdate,
    handleCalendarSearch,
    getTrayDataForCalendar,
    resetTrays,
    calendarCounts,
    selectedAttempt,
    setSelectedAttempt,
  } = useCalendarProviderViewModel({
    calendarSetId,
    calendarId: initialCalendarId,
    selectedUser,
  });

  const assignableUserIds = useMemo(
    () => assignableUsers?.map((user: IUserListItemResponse) => user.id) ?? [],
    [assignableUsers?.map((user: IUserListItemResponse) => user.id).join(",")]
  );

  const assignableUserIdsKey = useMemo(
    () => assignableUserIds.join(","),
    [assignableUserIds.join(",")]
  );

  const {
    isLoading: isSignalRLoading,
    connectionStatus,
    start,
    stop,
  } = useSignalR({
    calendarSetId: calendarSetId ?? null,
    assignableUserIds,
    dependencies: [activeCalendarId, assignableUsers, calendarSetId],
    onAttemptsUpdate,
  });

  const eventsList = Object.values(events);

  const value: CalendarContextType = useMemo(
    () => ({
      zoom,
      date,
      dateRange,
      events: eventsList,
      calendarSet: calendarSet ?? null,
      calendarConfig,
      activeCalendarId,
      assignableUsers,
      schedulerUsers,
      connectionStatus,
      isCalendarLoading: isCalendarLoading || isSignalRLoading,
      search,
      debouncedSearch,
      selectedUser,
      trayBuckets,
      allTrayEvents,
      selectedAttempt,
      calendarCounts,
      setSelectedAttempt,
      setZoom,
      setCalendarDate,
      setDateRange,
      handleCalendarSearch,
      handleSelectUser,
      refreshData: handleLoadEvents,
      getTrayDataForCalendar,
    }),
    [
      zoom,
      calendarConfig,
      calendarSet,
      activeCalendarId,
      assignableUsers,
      schedulerUsers,
      isCalendarLoading,
      isSignalRLoading,
      connectionStatus,
      events,
      date,
      dateRange,
      search,
      debouncedSearch,
      selectedUser,
      trayBuckets,
      allTrayEvents,
      selectedAttempt,
      calendarCounts,
    ]
  );

  useEffect(() => {
    if (calendarSetId) {
      start();
    }

    return () => {
      if (connectionStatus === HubStatuses.Connected) {
        stop();
      }
    };
  }, [calendarSetId, assignableUserIdsKey]);

  useEffect(() => {
    handleLoadEvents();
  }, [calendarConfig, calendarSetId, date, dateRange, selectedUser]);

  useEffect(() => {
    // TODO: Remove this logic check wehn trays are implemented nicely for other calendar types
    if (calendarSet?.config.eventType === CalendarEventType.Order) {
      getTrayDataForCalendar();
    }

    return () => {
      resetTrays();
    };
  }, [calendarSet?.config.id]);

  // TODO: Refreshing trays re-renders everything and that will include losing the tooltips and actions while using the calendar. Something like this should be within the trays directly if possible
  // useEffect(() => {
  //   if (calendarSet) {
  //     timerRef.current = setInterval(() => {
  //       getTrayDataForCalendar();
  //     }, getTotalMilliseconds(calendarSet.config.pollingPeriodSeconds));
  //   }

  //   return () => {
  //     if (timerRef.current) {
  //       clearInterval(timerRef.current);
  //       timerRef.current = null;
  //     }
  //     resetTrays();
  //   };
  // }, [calendarSet?.config.id]);

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendarContext = (): CalendarContextType => {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error("useCalendarContext must be used within CalendarProvider");
  }
  return context;
};

interface CalendarContextType {
  date: Moment;
  dateRange: IDateRangeISO;
  setCalendarDate: (newDate: Date | string | Moment) => void;
  setDateRange: (newRange: IDateRangeISO) => void;
  zoom: number;
  setZoom: (zoom: number) => void;
  events: IAttempt[];
  activeCalendarId?: string;
  calendarCounts: Record<string, number>;
  calendarSet: CalendarSetsConfigDataItem | null;
  calendarConfig: ICalendarConfig | null;
  assignableUsers?: IUserListItemResponse[];
  schedulerUsers?: IUserListItemResponse[];
  connectionStatus: HubStatuses;
  isCalendarLoading: boolean;
  search: string;
  debouncedSearch: string;
  handleCalendarSearch: (value: string) => void;
  handleSelectUser: (user: IUserListItemResponse | null) => void;
  selectedUser: IUserListItemResponse | null;
  refreshData: () => void;
  trayBuckets: IAttemptTrayConfig[];
  allTrayEvents:
    | IAttemptListResponse[]
    | IAttemptListItemResponseForWorkOrder[];
  selectedAttempt: IAttempt | null;
  setSelectedAttempt: (attempt: IAttempt | null) => void;
  getTrayDataForCalendar: () => Promise<void>;
}
interface CalendarProviderProps {
  children: ReactNode;
}
